import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import BookingApiUtils from '@utils/api/booking';
import { useFlashMessage } from '@root/hooks/use-flash-message';
import type {
  TutorCancelBookingModalContainerProps,
  CancelReasonOption,
} from './tutor-cancel-booking-modal.types';

export const useTutorCancelBookingModalService = (
  props: TutorCancelBookingModalContainerProps,
) => {
  const { hideModal, addModalMessage, clearModalMessage, booking } = props;

  const { t } = useTranslation('common');
  const { addFlash } = useFlashMessage();

  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reasonOptions: CancelReasonOption[] = useMemo(
    () => [
      { value: t('cancelReasons.onBehalfStudent'), id: '0' },
      { value: t('cancelReasons.unstableInternet'), id: '1' },
      { value: t('cancelReasons.blackout'), id: '2' },
      { value: t('cancelReasons.computerProblem'), id: '3' },
      { value: t('cancelReasons.sickness'), id: '4' },
      { value: t('cancelReasons.caringForFamily'), id: '5' },
      { value: t('cancelReasons.otherTutorReason'), id: '6' },
      { value: t('cancelReasons.lingualBoxReason'), id: '7' },
    ],
    [t],
  );

  const handleReasonChange = useCallback((value: string) => {
    setSelectedReason(value);
  }, []);

  const handleCommentChange = useCallback((value: string) => {
    setComment(value);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();

      if (!selectedReason || !comment) {
        addModalMessage('Reason and comment are required.', {
          autoDisappear: false,
          type: 'error',
        });
        return;
      }

      clearModalMessage();
      setIsSubmitting(true);

      try {
        await BookingApiUtils.cancelByTutor(booking.id, selectedReason, comment);

        addFlash('Successfully unscheduled the booking', {
          autoDisappear: true,
          sticky: true,
          type: 'success',
        });
        hideModal();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        addModalMessage(errorMessage, {
          autoDisappear: false,
          type: 'error',
        });
        setIsSubmitting(false);
      }
    },
    [
      selectedReason,
      comment,
      booking.id,
      addModalMessage,
      clearModalMessage,
      addFlash,
      hideModal,
    ],
  );

  const handleCancel = useCallback(() => {
    hideModal();
  }, [hideModal]);

  return {
    selectedReason,
    comment,
    reasonOptions,
    isSubmitting,
    message: props.message,
    messageType: props.messageType,
    onReasonChange: handleReasonChange,
    onCommentChange: handleCommentChange,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
  };
};
